import React from 'react';
import {
  CreditCardIcon, DevicePhoneMobileIcon, BuildingStorefrontIcon,
  ShoppingBagIcon, TicketIcon, UserIcon, BeakerIcon, QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';

/**
 * How a membership was paid for.
 *
 * One component so card looks like card everywhere. The colours are
 * deliberately not success/failure — every one of these is a membership that
 * happened, and tinting "paid at till" as a warning would read as a problem
 * when it is the most ordinary sale in the shop.
 */
export const METHODS = {
  card:     { label: 'Card',          Icon: CreditCardIcon,         color: '#2563EB' },
  whish:    { label: 'Whish',         Icon: DevicePhoneMobileIcon,  color: '#7C3AED' },
  store:    { label: 'Paid at till',  Icon: BuildingStorefrontIcon, color: '#0891B2' },
  shopify:  { label: 'Online store',  Icon: ShoppingBagIcon,        color: '#059669' },
  promo:    { label: 'Promo code',    Icon: TicketIcon,             color: '#D97706' },
  staff:    { label: 'Staff',         Icon: UserIcon,               color: '#64748B' },
  app:      { label: 'App activate',  Icon: DevicePhoneMobileIcon,  color: '#0D9488' },
  simulated:{ label: 'Test',          Icon: BeakerIcon,             color: '#94A3B8' },
};

// The activity log carries its own older source names. Map them onto the same
// vocabulary so one column does not speak two languages.
const ALIASES = {
  customer_self:    'card',
  promo_redeem:     'promo',
  staff_manual:     'staff',
  shopify_purchase: 'shopify',
  // Not 'staff'. Nobody at the shop did this: an existing VIP installed the
  // app and claimed the card they already had. Labelling it Staff credited a
  // member of the team with twelve activations they never touched, which is
  // exactly the sort of thing somebody is later measured on.
  app_activate:     'app',
  whish_simulated:  'whish',
  cybersource:      'card',
};

export function methodKey(raw) {
  if (!raw) return null;
  return ALIASES[raw] || raw;
}

export default function MethodBadge({ source, size = 12 }) {
  const key = methodKey(source);
  const m = METHODS[key];

  if (!m) {
    // Unknown, or a membership from before this was recorded. Say so plainly
    // rather than inventing a method.
    return (
      <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:size, color:'var(--text-subtle)' }}>
        <QuestionMarkCircleIcon style={{ width:size+2, height:size+2 }} />
        {source || 'Unknown'}
      </span>
    );
  }

  const { label, Icon, color } = m;
  return (
    <span style={{
      display:'inline-flex', alignItems:'center', gap:5,
      fontSize:size, fontWeight:600, color,
      background:`${color}14`, border:`1px solid ${color}33`,
      padding:'3px 8px', borderRadius:999, whiteSpace:'nowrap',
    }}>
      <Icon style={{ width:size+2, height:size+2 }} />
      {label}
    </span>
  );
}
